import { ColumnSizingState } from '@tanstack/react-table';
import { useEffect, useRef } from 'react';
import { useQueryParams } from '../../queryParams/useQueryParams';
import { useUserSelectBlockers } from '../../UserSelectBlocker';
import { useTableStoreValue } from '../TableContext';
import { useIsResizingColumn } from './useIsResizingColumn';

export const useColumnSizing = () => {
  const [table] = useTableStoreValue('table');
  const [persistColumnStyle] = useTableStoreValue('persistColumnStyle');
  const colgroupRef = useRef<HTMLTableColElement>(null);
  const isResizingColumn = useIsResizingColumn();
  const tableState = table.getState();
  const { columnSizing } = tableState;
  const { isInitialized } = tableState.columnSizingInfo;
  const [queryParams, setQueryParams] = useQueryParams();

  useEffect(() => {
    if (isInitialized || !queryParams.columnSizing || !persistColumnStyle) {
      return;
    }

    let columnSizing: ColumnSizingState;
    try {
      columnSizing = JSON.parse(queryParams.columnSizing) as ColumnSizingState;
    } catch (e) {
      console.error('Failed to parse column sizing from query params', e);
      return;
    }

    document
      .getElementById(table.options.name)
      ?.classList.add('column-sizing-enabled');

    table.setColumnSizing(columnSizing);
    table.setColumnSizingInfo((current) => ({
      ...current,
      isInitialized: true,
    }));
  }, [isInitialized, persistColumnStyle, queryParams.columnSizing, table]);

  useEffect(() => {
    if (isInitialized && isResizingColumn === false && persistColumnStyle) {
      setQueryParams({ columnSizing: JSON.stringify(columnSizing) });
    }
  }, [isResizingColumn, persistColumnStyle]);

  const { add: addUserSelectBlocker } = useUserSelectBlockers();
  useEffect(() => {
    if (isResizingColumn) {
      return addUserSelectBlocker();
    }
  }, [isResizingColumn, addUserSelectBlocker]);

  return { colgroupRef };
};

declare module '@tanstack/react-table' {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  export interface ColumnSizingInfoState {
    isInitialized?: boolean;
  }
}
